/**
 * Der Arbeitsplan am Projekt (Plan Projekt-Tracking, Etappe 6; Konzept § Der
 * Arbeitsplan am Projekt). Drei Schritte, von oben nach unten:
 *
 * 1. **Aus dem Angebot** aufteilen — Packzettel für Helfer gegen Pauschale, der
 *    Rest beim eigenen Team, Material kommt als Lieferung an einen Tag.
 * 2. **Tage** planen — Karten wie Termine im Planer; ein Tipp öffnet den Tag.
 * 3. **Im Kalender planen** — am Stück oder einzeln, erst Vorschau.
 *
 * Die Phase im Kopf rechnet der Server aus diesem Plan: solange ein Tag kein Datum
 * hat oder eine Leistung keinem Tag gehört, ist das Projekt „Zu planen".
 */
import { useState } from "react";
import { fehlerText } from "@vimaana/ui";

import ArbeitstagOeffnen from "./ArbeitstagBlatt";
import AufteilungBlock from "./AufteilungBlock";
import KalenderBlatt from "./KalenderBlatt";
import { useArbeitsplan, useArbeitstagAendern, useArbeitstagNeu, useVorschlaegeAlle } from "../../hooks/useArbeitsplan";
import { bedarfArt, positionsNummer, tagKurzWochentag, tagName, zeitspanne, type Arbeitsplan, type PlanTag } from "../../lib/arbeitsplan";
import "../../styles/arbeitsplan.css";
import "../../styles/invoice-editor.css";

export default function ArbeitsplanSektion({ projektId }: { projektId: string }) {
  const { data: plan, isPending, error } = useArbeitsplan(projektId);
  const neu = useArbeitstagNeu(projektId);
  const alle = useVorschlaegeAlle(projektId);
  const [offen, setOffen] = useState("");
  const [kalender, setKalender] = useState<string[] | null>(null);
  const [meldung, setMeldung] = useState("");

  if (isPending) return <div className="vg-detail-section-hint">Arbeitsplan wird geladen …</div>;
  if (error || !plan) return <p className="apl-fehler">{fehlerText(error)}</p>;

  const imKalender = plan.tage.filter((t) => t.im_kalender).length;
  const ohneDatum = plan.tage.length - imKalender;
  const leistungen = plan.angebot?.positionen || [];
  const verteilt = leistungen.filter((p) => p.tage.length).length;
  const anlegen = (nacharbeit: boolean) => neu.mutate(
    { nacharbeit, titel: nacharbeit ? "Nacharbeit" : undefined },
    { onSuccess: (d) => setOffen(d.id) },
  );

  return (
    <div className="apl">
      {plan.tage.length > 0 && (
        <div className="apl-fortschritt">
          <div className="apl-segmente" aria-hidden="true">
            {plan.tage.map((t) => (
              <span key={t.id} className={(t.im_kalender ? "is-kalender" : "is-offen") + (t.nacharbeit ? " is-nacharbeit" : "")} />
            ))}
          </div>
          <p>
            {imKalender} von {plan.tage.length} {plan.tage.length === 1 ? "Tag" : "Tagen"} im Kalender
            {leistungen.length > 0 && ` · ${verteilt} von ${leistungen.length} Leistungen an einem Tag`}
            {leistungen.length > 0 && plan.leistungsstand.erledigt > 0 && ` · ${plan.leistungsstand.erledigt} erledigt`}
          </p>
        </div>
      )}

      <div className="apl-knoepfe">
        <button type="button" className="vg-btn vg-btn-primary" disabled={neu.isPending} onClick={() => anlegen(false)}>+ Arbeitstag</button>
        <button type="button" className="vg-btn" disabled={neu.isPending} onClick={() => anlegen(true)}>+ Nacharbeit</button>
        {ohneDatum > 0 && (
          <button type="button" className="vg-btn" onClick={() => setKalender([])}>
            Im Kalender planen ({ohneDatum})
          </button>
        )}
      </div>
      {meldung && <p className="apl-meldung" role="status">{meldung}</p>}
      {plan.leistungsstand.abrechnungsbereit && (
        <p className="apl-bereit" role="status">
          <span>✓ Alle Tage vorbei{leistungen.length > 0 ? ", alle Leistungen abgehakt" : ""} — abrechnungsbereit.</span>
        </p>
      )}
      {plan.vorschlaege > 0 && (
        <div className="apl-vorschlag-banner">
          <span>
            {plan.vorschlaege === 1 ? "1 Lieferung oder Maschine" : `${plan.vorschlaege} Lieferungen und Maschinen`} aus dem Angebot
            {" "}vorgeschlagen — an den Tagen, an denen ihre Position steht.
          </span>
          <button type="button" className="vg-btn vg-btn-primary" disabled={alle.isPending} onClick={() => alle.mutate("")}>Alle übernehmen</button>
        </div>
      )}
      {alle.error && <p className="apl-fehler" role="alert">{fehlerText(alle.error)}</p>}
      {neu.error && <p className="apl-fehler" role="alert">{fehlerText(neu.error)}</p>}

      <AufteilungBlock plan={plan} />

      <OffeneLeistungen plan={plan} />

      {plan.tage.length === 0 ? (
        <p className="apl-leise">
          Noch kein Arbeitstag. „+ Arbeitstag“ legt Tag 1 an — ohne Datum. Leistungen, wer ausführt und was da
          sein muss, kommen an den Tag; in den Kalender geht es danach, einzeln oder am Stück.
        </p>
      ) : (
        <ol className="apl-tage">
          {plan.tage.map((t) => <TagKarte key={t.id} plan={plan} t={t} onOeffnen={() => setOffen(t.id)} />)}
        </ol>
      )}

      {offen && (
        <ArbeitstagOeffnen
          plan={plan}
          tagId={offen}
          onClose={() => setOffen("")}
          onKalender={(ids) => { setOffen(""); setKalender(ids); }}
          onOeffnen={setOffen}
        />
      )}
      {kalender && (
        <KalenderBlatt plan={plan} vorauswahl={kalender} onClose={() => setKalender(null)} onFertig={setMeldung} />
      )}
    </div>
  );
}

function TagKarte({ plan, t, onOeffnen }: { plan: Arbeitsplan; t: PlanTag; onOeffnen: () => void }) {
  const fehlt = t.im_kalender && !t.vorbei
    ? t.bedarf.filter((b) => b.projekt_material_id && !["bestellt", "geliefert"].includes(b.bestell_status))
    : [];
  const erledigt = t.positionen.filter((p) => p.erledigt_am || p.anderswo_erledigt).length;
  const nichtAbgehakt = t.vorbei ? t.positionen.length - erledigt : 0;
  return (
    <li className={"apl-tag" + (t.im_kalender ? "" : " is-ohne-datum") + (t.nacharbeit ? " is-nacharbeit" : "")}>
      <button type="button" className="apl-tag-knopf" onClick={onOeffnen} aria-label={`${tagName(t)} öffnen`}>
        <span className="apl-tag-kopf">
          <span className="apl-tag-nr">{tagName(t)}</span>
          <span className="apl-tag-datum">
            {t.im_kalender ? `${tagKurzWochentag(t.datum)} · ${zeitspanne(t.start_at, t.end_at)}` : "ohne Datum"}
          </span>
        </span>
        {t.titel && t.titel !== plan.titel && <span className="apl-tag-titel">{t.titel}</span>}
        <span className="apl-tag-wer">{t.wer}</span>
        {t.positionen.length > 0 ? (
          <span className="apl-tag-leistungen">
            {t.positionen.slice(0, 3).map((p) => (
              <span key={p.anker}>{p.bekannt ? `Pos. ${positionsNummer(plan, p.anker)} · ${p.titel}` : p.anker}</span>
            ))}
            {t.positionen.length > 3 && <span className="apl-leise">+ {t.positionen.length - 3} weitere</span>}
          </span>
        ) : (
          <span className="apl-leise">Noch keine Leistung.</span>
        )}
        {t.bedarf.length > 0 && (
          <span className="apl-tag-bedarf">
            {t.bedarf.map((b) => (
              <span key={b.id} className={b.erledigt ? "is-erledigt" : undefined}>
                {bedarfArt(b.kategorie).zeichen} {b.material_name || b.titel}
                {b.kategorie === "maschine" && b.bis_plan_nr ? ` · bis Tag ${b.bis_plan_nr}` : ""}
              </span>
            ))}
          </span>
        )}
        {t.vorschlaege.length > 0 && (
          <span className="apl-tag-vorschlag">
            {t.vorschlaege.length === 1 ? "1 Vorschlag" : `${t.vorschlaege.length} Vorschläge`}: {t.vorschlaege.map((v) => v.titel).join(", ")}
          </span>
        )}
        {t.besetzung.length > 0 && <span className="apl-tag-leute">{t.besetzung.map((p) => p.name).join(" · ")}</span>}
        {t.im_kalender && t.positionen.length > 0 && erledigt > 0 && nichtAbgehakt === 0 && (
          <span className="apl-tag-erledigt">✓ {erledigt === t.positionen.length ? "alles erledigt" : `${erledigt} von ${t.positionen.length} erledigt`}</span>
        )}
        {nichtAbgehakt > 0 && (
          <span className="apl-tag-warnung">
            {nichtAbgehakt === 1 ? "1 Leistung" : `${nichtAbgehakt} Leistungen`} nicht abgehakt — abhaken oder Nacharbeit planen
          </span>
        )}
        {fehlt.length > 0 && (
          <span className="apl-tag-warnung">
            {fehlt.map((b) => b.material_name || b.titel).join(", ")} {fehlt.length === 1 ? "ist" : "sind"} noch nicht bestellt
          </span>
        )}
      </button>
    </li>
  );
}

/** „Noch keinem Tag zugeordnet: Pos. 7 Tropfschlauch → Tag 1 · Tag 2" — ein Griff je Leistung. */
function OffeneLeistungen({ plan }: { plan: Arbeitsplan }) {
  const aendern = useArbeitstagAendern(plan.projekt_id);
  const [alle, setAlle] = useState(false);
  const offen = (plan.angebot?.positionen || []).filter((p) => !p.tage.length);
  if (!offen.length || !plan.tage.length) return null;
  const zeigen = alle ? offen : offen.slice(0, 5);
  const zu = (anker: string, tagId: string) => {
    const t = plan.tage.find((x) => x.id === tagId);
    if (t) aendern.mutate({ id: t.id, patch: { positionen: [...t.positionen.map((p) => p.anker), anker] } });
  };
  return (
    <div className="apl-offen">
      <div className="apl-cap">Noch keinem Tag zugeordnet · {offen.length}</div>
      <ul>
        {zeigen.map((p) => (
          <li key={p.id}>
            <span>Pos. {positionsNummer(plan, p.id)} · {p.titel}{p.packzettel ? ` · ${p.packzettel.nummer}` : ""}</span>
            <select aria-label={`${p.titel} zu einem Tag`} value="" disabled={aendern.isPending} onChange={(e) => zu(p.id, e.target.value)}>
              <option value="">zu Tag …</option>
              {plan.tage.map((t) => <option key={t.id} value={t.id}>{tagName(t)}{t.im_kalender ? ` · ${tagKurzWochentag(t.datum)}` : ""}</option>)}
            </select>
          </li>
        ))}
      </ul>
      {offen.length > 5 && (
        <button type="button" className="vg-btn" onClick={() => setAlle(!alle)}>{alle ? "Weniger zeigen" : `Alle ${offen.length} zeigen`}</button>
      )}
      {aendern.error && <p className="apl-fehler" role="alert">{fehlerText(aendern.error)}</p>}
    </div>
  );
}
